import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import { SmartToy, CheckCircle } from '@mui/icons-material';
import { mockProjects } from '../../data/mockData';

interface InviteNotetakerDialogProps {
  open: boolean;
  onClose: () => void;
}

export const InviteNotetakerDialog: React.FC<InviteNotetakerDialogProps> = ({ open, onClose }) => {
  const [platform, setPlatform] = useState('meet');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [selectedProject, setSelectedProject] = useState(mockProjects[1]?.id || '');
  const [autoJira, setAutoJira] = useState(true);
  const [autoBrd, setAutoBrd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleDispatch = () => {
    if (!meetingUrl) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setMeetingUrl('');
        onClose();
      }, 1600);
    }, 1200);
  };

  const handleClose = () => {
    if (!loading) {
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            bgcolor: 'var(--hp-primary-light)',
            color: 'var(--hp-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <SmartToy />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
            Invite ScrumAI Notetaker
          </Typography>
          <Typography variant="caption" color="var(--hp-text-secondary)">
            ScrumAI joins your meeting, takes smart notes, extracts decisions, and creates Jira items.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2.5 }}>
        {success ? (
          <Alert severity="success" icon={<CheckCircle fontSize="inherit" />} sx={{ borderRadius: '12px' }}>
            <strong>ScrumAI Notetaker Dispatched!</strong> Joining meeting room now. Transcripts & Jira actions will sync live to your workspace.
          </Alert>
        ) : (
          <>
            <FormControl fullWidth size="small">
              <InputLabel id="platform-label">Meeting Platform</InputLabel>
              <Select
                labelId="platform-label"
                value={platform}
                label="Meeting Platform"
                onChange={(e) => setPlatform(e.target.value)}
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="meet">Google Meet (meet.google.com/...)</MenuItem>
                <MenuItem value="zoom">Zoom Video (zoom.us/j/...)</MenuItem>
                <MenuItem value="teams">Microsoft Teams</MenuItem>
                <MenuItem value="webex">Cisco Webex</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Meeting URL or Invitation Link"
              placeholder="https://meet.google.com/abc-defg-hij"
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              required
              helperText="Paste the complete invite link"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="project-label">Associated Jira Project</InputLabel>
              <Select
                labelId="project-label"
                value={selectedProject}
                label="Associated Jira Project"
                onChange={(e) => setSelectedProject(e.target.value)}
                sx={{ borderRadius: '12px' }}
              >
                {mockProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ bgcolor: 'var(--hp-surface)', p: 2, borderRadius: '14px', border: '1px solid var(--hp-border)' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'var(--hp-text)' }}>
                Autonomous AI Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={<Switch checked={autoJira} onChange={(e) => setAutoJira(e.target.checked)} color="primary" />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Auto-create Jira Epics & User Stories</Typography>
                      <Typography variant="caption" color="var(--hp-text-secondary)">Detects commitments and generates backlog tickets with acceptance criteria</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={<Switch checked={autoBrd} onChange={(e) => setAutoBrd(e.target.checked)} color="primary" />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Generate BRD & Executive Summary</Typography>
                      <Typography variant="caption" color="var(--hp-text-secondary)">Builds formal requirement documents ready for stakeholder review</Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none', color: 'var(--hp-text-secondary)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!meetingUrl || loading || success}
          onClick={handleDispatch}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SmartToy />}
          sx={{
            bgcolor: 'var(--hp-primary)',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
          }}
        >
          {loading ? 'Connecting Bot...' : 'Dispatch Notetaker'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteNotetakerDialog;
